/**
 * Volunteer Dashboard Tab Component
 * Displays summary cards, alerts, and recent activity
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Users, Shield, Clock, ClipboardCheck, AlertTriangle,
  AlertCircle, Phone, FileText, CheckCircle
} from "lucide-react";
import { STATUS_CONFIG } from "./constants";

const VolunteerDashboardTab = ({
  isDark,
  dashboard,
  missedVisits,
  weeklyCheckins,
  idtSummaries,
  visits,
  setActiveTab
}) => {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="active-volunteers-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Active Volunteers</p>
                <p className="text-3xl font-bold text-teal-500">{dashboard?.summary?.active_volunteers || 0}</p>
              </div>
              <Users className="w-10 h-10 text-teal-500/30" />
            </div>
            <p className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {dashboard?.summary?.total_volunteers || 0} total in system
            </p>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="compliance-rate-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Compliance Rate</p>
                <p className="text-3xl font-bold text-green-500">{dashboard?.compliance?.compliance_rate || 0}%</p>
              </div>
              <Shield className="w-10 h-10 text-green-500/30" />
            </div>
            <Progress 
              value={dashboard?.compliance?.compliance_rate || 0} 
              className="h-2 mt-3"
            />
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="monthly-hours-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Monthly Hours</p>
                <p className="text-3xl font-bold text-blue-500">{dashboard?.visits?.monthly_hours || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-500/30" />
            </div>
            <p className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {dashboard?.visits?.ytd_hours || 0} YTD hours
            </p>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="active-assignments-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Active Assignments</p>
                <p className="text-3xl font-bold text-purple-500">{dashboard?.summary?.active_assignments || 0}</p>
              </div>
              <ClipboardCheck className="w-10 h-10 text-purple-500/30" />
            </div>
            <p className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {dashboard?.summary?.unassigned_patients || 0} patients need volunteers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-lg">Volunteer Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(dashboard?.summary?.status_breakdown || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG[status]?.color || "bg-gray-500"}`} />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      {STATUS_CONFIG[status]?.label || status}
                    </span>
                  </div>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alerts & Actions */}
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Attention Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {missedVisits.length > 0 && (
                <AlertItem
                  isDark={isDark}
                  color="red"
                  icon={<AlertCircle className="w-4 h-4 text-red-500" />}
                  text={`${missedVisits.length} Missed Visits`}
                  onView={() => setActiveTab("visits")}
                />
              )}
              
              {weeklyCheckins.filter(c => c.priority === "high").length > 0 && (
                <AlertItem
                  isDark={isDark}
                  color="yellow"
                  icon={<Phone className="w-4 h-4 text-yellow-500" />}
                  text={`${weeklyCheckins.filter(c => c.priority === "high").length} Overdue Check-ins`}
                  buttonText="Call List"
                />
              )}
              
              {(dashboard?.compliance?.incomplete || 0) > 0 && (
                <AlertItem
                  isDark={isDark}
                  color="orange"
                  icon={<Shield className="w-4 h-4 text-orange-500" />}
                  text={`${dashboard?.compliance?.incomplete || 0} Incomplete Compliance`}
                  onView={() => setActiveTab("compliance")}
                />
              )}
              
              {idtSummaries.length > 0 && (
                <AlertItem
                  isDark={isDark}
                  color="blue"
                  icon={<FileText className="w-4 h-4 text-blue-500" />}
                  text={`${idtSummaries.length} IDT Summaries Pending Review`}
                  buttonText="Review"
                  onView={() => setActiveTab("idt")}
                />
              )}
              
              {missedVisits.length === 0 && 
               weeklyCheckins.filter(c => c.priority === "high").length === 0 && 
               (dashboard?.compliance?.incomplete || 0) === 0 &&
               idtSummaries.length === 0 && (
                <div className={`p-3 rounded-lg ${isDark ? "bg-green-500/10" : "bg-green-50"}`}>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-green-500">All tasks up to date!</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-lg">Recent Visits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {visits.slice(0, 5).map((visit) => (
              <div 
                key={visit.id}
                className={`p-3 rounded-lg flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
              >
                <div>
                  <p className="font-medium">{visit.volunteer_name}</p>
                  <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {visit.visit_date} • {visit.duration_minutes} min
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {visit.activities?.join(", ") || "Visit"}
                  </p>
                </div>
              </div>
            ))}
            {visits.length === 0 && (
              <p className={`text-center py-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                No recent visits
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for alert items
const AlertItem = ({ isDark, color, icon, text, buttonText = "View", onView }) => {
  const colorClasses = {
    red: isDark ? "bg-red-500/10 border border-red-500/20" : "bg-red-50 border border-red-200",
    yellow: isDark ? "bg-yellow-500/10 border border-yellow-500/20" : "bg-yellow-50 border border-yellow-200",
    orange: isDark ? "bg-orange-500/10 border border-orange-500/20" : "bg-orange-50 border border-orange-200",
    blue: isDark ? "bg-blue-500/10 border border-blue-500/20" : "bg-blue-50 border border-blue-200"
  };
  
  const textColors = {
    red: "text-red-500",
    yellow: "text-yellow-500",
    orange: "text-orange-500",
    blue: "text-blue-500"
  };

  return (
    <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className={`${textColors[color]} font-medium`}>{text}</span>
        </div>
        {onView && (
          <Button 
            variant="ghost" 
            size="sm" 
            className={textColors[color]}
            onClick={onView}
          >
            {buttonText}
          </Button>
        )}
      </div>
    </div>
  );
};

export default VolunteerDashboardTab;
